export type EmployerScoreTier = 'A' | 'B' | 'C' | 'D' | 'E';

/**
 * Coarse letter grades for the 0-100 Employer Score, so the public-facing display never states
 * a specific number an employer could dispute. Purely a display-layer mapping: the underlying
 * score still drives ranking, sorting, and the isNetworkWorthy threshold exactly as before, this
 * only changes what gets rendered to users. Five even 20-point bands, so a neutral, no-data score
 * (50) lands solidly in the middle grade (C) rather than skewing toward either end.
 */
const TIER_BANDS: ReadonlyArray<{ min: number; tier: EmployerScoreTier }> = [
  { min: 80, tier: 'A' },
  { min: 60, tier: 'B' },
  { min: 40, tier: 'C' },
  { min: 20, tier: 'D' },
  { min: 0, tier: 'E' },
];

export function mapEmployerScoreToTier(score: number): EmployerScoreTier {
  const clamped = Math.max(0, Math.min(100, score));
  const band = TIER_BANDS.find((b) => clamped >= b.min);
  return band ? band.tier : 'E';
}

/** Ordinal rank for comparing two tiers (higher = better), e.g. for score-change badges. */
const TIER_RANK: Record<EmployerScoreTier, number> = {
  E: 0,
  D: 1,
  C: 2,
  B: 3,
  A: 4,
};

export function compareEmployerScoreTiers(a: EmployerScoreTier, b: EmployerScoreTier): number {
  return TIER_RANK[a] - TIER_RANK[b];
}
